import React, { useState, useMemo, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

import MainLayout from '../../components/layout/MainLayout';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import DeliveryModeSelector from './components/DeliveryModeSelector';
import EngineerSelector from './components/EngineerSelector';
import LicenseSelector from './components/LicenseSelector';
import MaterialSelector from './components/MaterialSelector';
import OutsourceSelector from './components/OutsourceSelector';
import ConfigurationPanel from './components/ConfigurationPanel';
import CalculationResults from './components/CalculationResults';
import SaveTemplateModal from './components/SaveTemplateModal';
import TemplateLibrary from './components/TemplateLibrary';
import EstimateHistory from './components/EstimateHistory';
import SaveEstimateModal from './components/SaveEstimateModal';
import ComparisonView from './components/ComparisonView';
import WhatIfScenarioManager from './components/WhatIfScenarioManager';
import ExportModal from './components/ExportModal';
import CloseWonModal from './components/CloseWonModal';

import { saveEstimateToDB, updateEstimateInDB } from '../../services/dataService';
import { useAuth } from '../../contexts/AuthContext';
import { analyzeEstimate } from '../../services/openaiService';
import AIRecommendationsModal from './components/AIRecommendationsModal';
import { logAuditEvent, AUDIT_ACTIONS, AUDIT_ENTITIES } from '../../services/auditLogService';
import { syncTicketWithCalculator, checkTicketExistsForCalculation } from '../../services/incentiveTicketsService';
import { useNavigate } from 'react-router-dom';
import MasterDataSyncDialog from './components/MasterDataSyncDialog';
import { getEngineers, getMaterials, getSoftwareLicenses, getOutsourceResources } from '../../services/masterDataService';

const CURRENCIES = [
  { code: 'OMR', symbol: 'OMR', name: 'Omani Rial' },
  { code: 'USD', symbol: '$', name: 'US Dollar' },
  { code: 'EUR', symbol: '€', name: 'Euro' },
  { code: 'GBP', symbol: '£', name: 'British Pound' },
  { code: 'AED', symbol: 'AED', name: 'UAE Dirham' },
  { code: 'SAR', symbol: 'SAR', name: 'Saudi Riyal' },
  { code: 'QAR', symbol: 'QAR', name: 'Qatari Riyal' },
  { code: 'KWD', symbol: 'KWD', name: 'Kuwaiti Dinar' },
  { code: 'BHD', symbol: 'BHD', name: 'Bahraini Dinar' },
  { code: 'JOD', symbol: 'JOD', name: 'Jordanian Dinar' },
];

const STORAGE_KEY = 'sc_session_state';

const loadSessionState = (userId) => {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      // Only restore if the stored state belongs to the current user
      if (parsed?._userId && parsed?._userId !== userId) {
        sessionStorage.removeItem(STORAGE_KEY);
        return null;
      }
      return parsed;
    }
  } catch (_) {}
  return null;
};

const ServiceCalculator = () => {
  const location = useLocation();
  const { user, permissions, isViewer } = useAuth();
  const navigate = useNavigate();

  const _saved = loadSessionState(user?.id);

  const [deliveryMode, setDeliveryMode] = useState(_saved?.deliveryMode || 'onsite');
  const [selectedEngineers, setSelectedEngineers] = useState(_saved?.selectedEngineers || []);
  const [selectedLicenses, setSelectedLicenses] = useState(_saved?.selectedLicenses || []);
  const [selectedMaterials, setSelectedMaterials] = useState(_saved?.selectedMaterials || []);
  const [selectedOutsource, setSelectedOutsource] = useState(_saved?.selectedOutsource || []);
  const [config, setConfig] = useState(_saved?.config || {
    overhead: 15,
    targetMargin: 25,
    contingency: 8
  });
  const [currency, setCurrency] = useState(_saved?.currency || 'OMR');
  const [showResults, setShowResults] = useState(_saved?.showResults || false);
  const [showSaveTemplateModal, setShowSaveTemplateModal] = useState(false);
  const [showTemplateLibrary, setShowTemplateLibrary] = useState(false);
  const [showEstimateHistory, setShowEstimateHistory] = useState(false);
  const [showSaveEstimateModal, setShowSaveEstimateModal] = useState(false);
  const [showAIRecommendations, setShowAIRecommendations] = useState(false);
  const [showComparisonView, setShowComparisonView] = useState(false);
  const [showWhatIfScenarios, setShowWhatIfScenarios] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);
  const [aiAnalysis, setAiAnalysis] = useState(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [lastSavedPtcName, setLastSavedPtcName] = useState('');
  const [lastSavedPtcNumber, setLastSavedPtcNumber] = useState('');
  const [validationError, setValidationError] = useState('');
  const [loadedEstimateId, setLoadedEstimateId] = useState(null);
  const [loadedEstimate, setLoadedEstimate] = useState(null);
  const [showCloseWonModal, setShowCloseWonModal] = useState(false);
  const [existingTicket, setExistingTicket] = useState(null);

  // Master Data Sync state
  const [syncChanges, setSyncChanges] = useState([]);
  const [showSyncBanner, setShowSyncBanner] = useState(false);
  const [showSyncDialog, setShowSyncDialog] = useState(false);
  const [syncBannerMinimized, setSyncBannerMinimized] = useState(false);

  const userProfile = user?.profile || user || null;

  const selectedCurrency = CURRENCIES?.find(c => c?.code === currency) || CURRENCIES?.[0];

  const deliveryModeMultipliers = {
    onsite: 1.0,
    remote: 0.85,
    hybrid: 0.92
  };

  const calculations = useMemo(() => {
    const modeMultiplier = deliveryModeMultipliers?.[deliveryMode];

    // Derive pricing basis from the dominant timeUnit of selected engineers
    const dominantUnit = selectedEngineers?.length > 0
      ? (selectedEngineers?.[0]?.timeUnit || 'hours')
      : 'hours';
    const pricingBasis = dominantUnit === 'days' ? 'daily' : dominantUnit === 'months' ? 'monthly' : 'hourly';

    // Productive hours based on pricing basis
    const productiveHoursPerMonth =
      pricingBasis === 'daily' ? 9 :
      pricingBasis === 'monthly' ? 240 :
      160; // hourly default

    const totalEngineerCosts = selectedEngineers?.reduce((sum, eng) => {
      const baseCost = (eng?.count || 1) * (eng?.hours || 0) * (eng?.costPerHour || 0) * modeMultiplier;
      const timeUnit = eng?.timeUnit || 'hours';
      const rawSpf = eng?.nationality === 'omani' ? (eng?.spfValue ?? 0) : 0;
      const rawHI = eng?.healthInsuranceEnabled ? (eng?.healthInsuranceValue ?? 0) : 0;
      // Prorate monthly SPF/HI to match the selected time unit, then multiply by duration and count
      const prorateMonthly = (val) => {
        if (!val) return 0;
        if (timeUnit === 'days') return val / 30;
        if (timeUnit === 'hours') return val / 30 / 9;
        return val; // months
      };
      const spfAmount = prorateMonthly(rawSpf) * (eng?.duration || 1) * (eng?.count || 1);
      const hiAmount = prorateMonthly(rawHI) * (eng?.duration || 1) * (eng?.count || 1);
      return sum + baseCost + spfAmount + hiAmount;
    }, 0);

    const totalEngineerSpf = selectedEngineers?.reduce((sum, eng) => {
      const timeUnit = eng?.timeUnit || 'hours';
      const rawSpf = eng?.nationality === 'omani' ? (eng?.spfValue ?? 0) : 0;
      const prorateMonthly = (val) => {
        if (!val) return 0;
        if (timeUnit === 'days') return val / 30;
        if (timeUnit === 'hours') return val / 30 / 9;
        return val;
      };
      return sum + prorateMonthly(rawSpf) * (eng?.duration || 1) * (eng?.count || 1);
    }, 0);

    const totalEngineerHealthInsurance = selectedEngineers?.reduce((sum, eng) => {
      const timeUnit = eng?.timeUnit || 'hours';
      const rawHI = eng?.healthInsuranceEnabled ? (eng?.healthInsuranceValue ?? 0) : 0;
      const prorateMonthly = (val) => {
        if (!val) return 0;
        if (timeUnit === 'days') return val / 30;
        if (timeUnit === 'hours') return val / 30 / 9;
        return val;
      };
      return sum + prorateMonthly(rawHI) * (eng?.duration || 1) * (eng?.count || 1);
    }, 0);

    const totalMaterialsCost = selectedMaterials?.reduce((sum, mat) => {
      const baseCost = mat?.quantity * mat?.unitCost;
      const wasteCost = baseCost * (mat?.wastePercentage / 100);
      return sum + baseCost + wasteCost;
    }, 0);

    const totalLicensesCosts = selectedLicenses?.reduce((sum, lic) => {
      if (lic?.type === 'perpetual') {
        return sum + (lic?.oneTimeCost * lic?.quantity);
      }
      return sum + (lic?.monthlyCost * lic?.quantity * lic?.duration);
    }, 0);

    const totalOutsourceCosts = selectedOutsource?.reduce((sum, r) => {
      // Use pre-calculated totalCost if available (includes SPF + Health Insurance)
      if (r?.totalCost !== undefined) return sum + (r?.totalCost || 0);
      const hours = r?.hours || ((r?.duration || 1) * (r?.timeUnit === 'days' ? 9 : r?.timeUnit === 'months' ? 240 : 1));
      return sum + ((r?.quantity || 1) * hours * (r?.costPerHour || r?.rate || 0));
    }, 0);

    const directCosts = totalEngineerCosts + totalMaterialsCost + totalLicensesCosts + totalOutsourceCosts;
    const overheadCosts = directCosts * (config?.overhead / 100);
    const contingencyCosts = (directCosts + overheadCosts) * (config?.contingency / 100);
    const totalCost = directCosts + overheadCosts + contingencyCosts;
    const margin = (config?.targetMargin || 0) / 100;
    const sellPrice = margin < 1 ? totalCost / (1 - margin) : totalCost * (1 + margin);
    const grossProfit = sellPrice - totalCost;
    const profitMarginPercentage = (grossProfit / sellPrice) * 100;

    const costPerProductiveHour = totalEngineerCosts > 0 
      ? totalEngineerCosts / (selectedEngineers?.reduce((sum, eng) => sum + (eng?.count * eng?.hours), 0) || 1)
      : 0;

    return {
      productiveHoursPerMonth,
      costPerProductiveHour,
      totalEngineerCosts,
      totalEngineerSpf,
      totalEngineerHealthInsurance,
      totalMaterialsCost,
      totalLicensesCosts,
      totalOutsourceCosts,
      directCosts,
      overheadCosts,
      contingencyCosts,
      totalCost,
      sellPrice,
      grossProfit,
      profitMarginPercentage,
      overheadPercentage: config?.overhead,
      contingencyPercentage: config?.contingency,
      marginPercentage: config?.targetMargin,
      pricingBasis,
    };
  }, [deliveryMode, selectedEngineers, selectedLicenses, selectedMaterials, selectedOutsource, config]);

  // Check ticket status whenever a saved estimate is loaded/edited
  useEffect(() => {
    if (!loadedEstimateId) {
      setExistingTicket(null);
      return;
    }
    checkTicketExistsForCalculation(loadedEstimateId)?.then(ticket => setExistingTicket(ticket || null))?.catch(() => setExistingTicket(null));
  }, [loadedEstimateId]);

  // ─── AUTO-SYNC: silently push calculator changes to the linked incentive ticket ───
  // Runs whenever `calculations` changes while an estimate with a linked ticket is loaded.
  // Debounced 1.5 s so rapid slider/input changes don't flood the DB.
  const _autoSyncTimerRef = React.useRef(null);
  useEffect(() => {
    // Only sync when we have a saved estimate AND a linked ticket
    if (!loadedEstimateId || !existingTicket || !user?.id) return;

    // Clear any pending debounce
    if (_autoSyncTimerRef?.current) clearTimeout(_autoSyncTimerRef?.current);

    _autoSyncTimerRef.current = setTimeout(async () => {
      try {
        const syncData = {
          projectValue: calculations?.sellPrice || 0,
          contractValue: calculations?.sellPrice || 0,
          grossProfit: calculations?.grossProfit || 0,
          totalCost: calculations?.totalCost || 0,
          targetMarginPercentage: config?.targetMargin || 0,
          projectName: loadedEstimate?.name || loadedEstimate?.projectName || '',
          clientName: loadedEstimate?.clientName || loadedEstimate?.client || '',
        };
        await syncTicketWithCalculator(
          loadedEstimateId,
          syncData,
          user?.id,
          user?.profile?.full_name || user?.email || ''
        );
      } catch (syncErr) {
        // Silent — auto-sync failures should not interrupt the user
        console.warn('[ServiceCalculator] Auto-sync warning:', syncErr?.message);
      }
    }, 1500);

    return () => {
      if (_autoSyncTimerRef?.current) clearTimeout(_autoSyncTimerRef?.current);
    };
  }, [calculations, loadedEstimateId, existingTicket, user?.id]);
  // ─────────────────────────────────────────────────────────────────────────────────

  // Persist core calculator state to sessionStorage on every change
  useEffect(() => {
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify({
        _userId: user?.id,
        deliveryMode,
        selectedEngineers,
        selectedLicenses,
        selectedMaterials,
        selectedOutsource,
        config,
        currency,
        showResults,
      }));
    } catch (_) {}
  }, [deliveryMode, selectedEngineers, selectedLicenses, selectedMaterials, selectedOutsource, config, currency, showResults]);

  const handleCalculate = () => {
    setValidationError('');
    setShowResults(true);
  };

  // Auto-show results whenever any calculator input changes (after initial calculate)
  useEffect(() => {
    if (
      selectedEngineers?.length > 0 ||
      selectedLicenses?.length > 0 ||
      selectedMaterials?.length > 0 ||
      selectedOutsource?.length > 0
    ) {
      setShowResults(true);
    }
  }, [deliveryMode, selectedEngineers, selectedLicenses, selectedMaterials, selectedOutsource, config]);

  const handleReset = () => {
    try { sessionStorage.removeItem(STORAGE_KEY); } catch (_) {}
    setDeliveryMode('onsite');
    setSelectedEngineers([]);
    setSelectedLicenses([]);
    setSelectedMaterials([]);
    setSelectedOutsource([]);
    setConfig({ overhead: 15, targetMargin: 25, contingency: 8 });
    setCurrency('OMR');
    setShowResults(false);
    setLoadedEstimateId(null);
    setLoadedEstimate(null);
  };

  const handleGeneratePDF = () => {
    setShowExportModal(true);
  };

  const handleSaveTemplate = (templateData) => {
    const saveTemplate = (data) => {
      try {
        const templates = JSON.parse(localStorage.getItem('serviceCalculatorTemplates') || '[]');
        const newTemplate = { ...data, id: Date.now(), createdAt: new Date()?.toISOString() };
        templates?.push(newTemplate);
        localStorage.setItem('serviceCalculatorTemplates', JSON.stringify(templates));
        return newTemplate;
      } catch (e) {
        console.error('Error saving template:', e);
        return null;
      }
    };
    const template = saveTemplate({
      ...templateData,
      config: {
        deliveryMode,
        selectedEngineers,
        selectedLicenses,
        selectedMaterials,
        config
      }
    });
    if (template) {
      alert('Template saved successfully!');
    }
  };

  const handleLoadTemplate = (template) => {
    const templateConfig = template?.config;
    setDeliveryMode(templateConfig?.deliveryMode || 'onsite');
    setSelectedEngineers(templateConfig?.selectedEngineers || []);
    setSelectedLicenses(templateConfig?.selectedLicenses || []);
    setSelectedMaterials(templateConfig?.selectedMaterials || []);
    setConfig(templateConfig?.config || { overhead: 15, targetMargin: 25, contingency: 8 });
    setShowResults(false);
  };

  const handleSaveEstimate = async (estimateData) => {
    if (!user) {
      alert('You must be logged in to save estimates.');
      return;
    }
    try {
      const payload = {
        ...estimateData,
        deliveryMode,
        selectedEngineers,
        selectedLicenses,
        selectedMaterials,
        selectedOutsource,
        config,
        currency,
        calculations
      };

      let saved;
      if (loadedEstimateId) {
        // Update existing estimate
        saved = await updateEstimateInDB(loadedEstimateId, payload);

        // Trigger calculator sync — update linked incentive ticket if it exists
        try {
          const syncData = {
            projectValue: calculations?.sellPrice || 0,
            contractValue: calculations?.sellPrice || 0,
            grossProfit: calculations?.grossProfit || 0,
            totalCost: calculations?.totalCost || 0,
            targetMarginPercentage: config?.targetMargin || 0,
            projectName: estimateData?.name || '',
            clientName: estimateData?.clientName || '',
          };
          await syncTicketWithCalculator(
            loadedEstimateId,
            syncData,
            user?.id,
            user?.profile?.full_name || user?.email || ''
          );
        } catch (syncErr) {
          console.warn('[ServiceCalculator] Sync warning:', syncErr?.message);
        }
        alert('Estimate updated successfully!');
      } else {
        // Create new estimate — NO auto ticket creation. Ticket is created only via Close Won.
        saved = await saveEstimateToDB(payload, user?.id);
        setLoadedEstimateId(saved?.id);
        setLoadedEstimate(saved);
        alert('Calculation saved successfully. To create an Incentive Ticket, open Estimate History and click "Mark as Close Won & Create Incentive Ticket".');
      }

      // Store the saved PTC Name and PTC Number for export auto-population
      setLastSavedPtcName(estimateData?.name || '');
      setLastSavedPtcNumber(estimateData?.ptcNumber || '');
      // Audit log
      logAuditEvent({
        user,
        userProfile,
        action: loadedEstimateId ? AUDIT_ACTIONS?.ESTIMATE_UPDATED || AUDIT_ACTIONS?.ESTIMATE_CREATED : AUDIT_ACTIONS?.ESTIMATE_CREATED,
        entityType: AUDIT_ENTITIES?.ESTIMATE,
        entityId: saved?.id || null,
        entityName: estimateData?.name || 'Unnamed Estimate',
        newValue: {
          ptcNumber: estimateData?.ptcNumber,
          deliveryMode,
          currency,
          totalCost: calculations?.totalCost,
        },
      });
    } catch (err) {
      console.error('Error saving estimate:', err);
      alert('Failed to save estimate. Please try again.');
    }
  };

  const handleLoadEstimate = (estimate) => {
    setDeliveryMode(estimate?.deliveryMode || 'onsite');
    setSelectedEngineers(estimate?.selectedEngineers || []);
    setSelectedLicenses(estimate?.selectedLicenses || []);
    setSelectedMaterials(estimate?.selectedMaterials || []);
    setSelectedOutsource(estimate?.selectedOutsource || []);
    setConfig(estimate?.config || { overhead: 15, targetMargin: 25, contingency: 8 });
    if (estimate?.currency) setCurrency(estimate?.currency);
    // Restore PTC Name and PTC Number so export auto-population works after loading
    setLastSavedPtcName(estimate?.name || estimate?.ptcName || '');
    setLastSavedPtcNumber(estimate?.ptcNumber || '');
    // Clear any previously tracked loaded estimate (load-only = view, no auto-update)
    setLoadedEstimateId(null);
    setLoadedEstimate(null);
    setShowResults(true);
    // Trigger Master Data sync check
    detectMasterDataChanges(estimate);
  };

  const handleEditEstimate = (estimate) => {
    // Load all data AND track the estimate ID so Save will update the same record
    setDeliveryMode(estimate?.deliveryMode || 'onsite');
    setSelectedEngineers(estimate?.selectedEngineers || []);
    setSelectedLicenses(estimate?.selectedLicenses || []);
    setSelectedMaterials(estimate?.selectedMaterials || []);
    setSelectedOutsource(estimate?.selectedOutsource || []);
    setConfig(estimate?.config || { overhead: 15, targetMargin: 25, contingency: 8 });
    if (estimate?.currency) setCurrency(estimate?.currency);
    setLastSavedPtcName(estimate?.name || '');
    setLastSavedPtcNumber(estimate?.ptcNumber || '');
    setLoadedEstimateId(estimate?.id);
    setLoadedEstimate(estimate);
    setShowResults(true);
    // Trigger Master Data sync check
    detectMasterDataChanges(estimate);
  };

  // ─── MASTER DATA SYNC DETECTION ───────────────────────────────────────────────
  const detectMasterDataChanges = async (estimate) => {
    try {
      const [currentEngineers, currentMaterials, currentLicenses, currentOutsource] = await Promise.all([
        getEngineers(),
        getMaterials(),
        getSoftwareLicenses(),
        getOutsourceResources(),
      ]);

      const changes = [];

      // Check Engineers
      (estimate?.selectedEngineers || [])?.forEach((savedEng) => {
        // Use masterId if present (entries use id=generated, masterId=DB id)
        const lookupId = savedEng?.masterId || savedEng?.id;
        // Primary lookup by ID; fallback to name match for pre-masterId estimates
        let current = currentEngineers?.find(e => e?.id === lookupId);
        if (!current) {
          const fallbackName = savedEng?.roleLabel || savedEng?.name;
          if (fallbackName) current = currentEngineers?.find(e => e?.name?.toLowerCase() === fallbackName?.toLowerCase());
        }
        if (!current) return;
        const changedFields = [];
        const fieldsToCheck = ['monthlySalary', 'spfValue', 'spfPercentage', 'healthInsuranceEnabled', 'healthInsuranceValue', 'utilizationDefault'];
        fieldsToCheck?.forEach(key => {
          const savedVal = savedEng?.[key];
          const currentVal = current?.[key];
          if (key === 'healthInsuranceEnabled') {
            if (savedVal !== currentVal) {
              changedFields?.push({ key, savedValue: savedVal, currentValue: currentVal });
            }
          } else if (savedVal !== undefined && currentVal !== undefined && Math.abs(Number(savedVal) - Number(currentVal)) > 0.0001) {
            changedFields?.push({ key, savedValue: savedVal, currentValue: currentVal });
          }
        });
        // Check non-numeric fields: role, specialization, nationality
        ['role', 'specialization', 'nationality']?.forEach(key => {
          const savedVal = savedEng?.[key];
          const currentVal = current?.[key];
          if (savedVal !== undefined && currentVal !== undefined && savedVal !== currentVal) {
            changedFields?.push({ key, savedValue: savedVal, currentValue: currentVal });
          }
        });
        // Also compare monthlySalary even if savedVal was not stored — compare against current
        if (!fieldsToCheck?.includes('monthlySalary') || savedEng?.monthlySalary === undefined) {
          // skip — already handled above
        }
        if (changedFields?.length > 0) {
          changes?.push({ id: current?.id, name: savedEng?.name || savedEng?.roleLabel || current?.name, type: 'engineer', changedFields, currentMasterData: current });
        }
      });

      // Check Materials
      (estimate?.selectedMaterials || [])?.forEach((savedMat) => {
        const lookupId = savedMat?.masterId || savedMat?.id;
        let current = currentMaterials?.find(m => m?.id === lookupId);
        if (!current) {
          const fallbackName = savedMat?.itemName;
          if (fallbackName) current = currentMaterials?.find(m => m?.itemName?.toLowerCase() === fallbackName?.toLowerCase());
        }
        if (!current) return;
        const changedFields = [];
        ['unitCost', 'wastePercentage']?.forEach(key => {
          const savedVal = savedMat?.[key];
          const currentVal = current?.[key];
          if (savedVal !== undefined && currentVal !== undefined && Math.abs(Number(savedVal) - Number(currentVal)) > 0.0001) {
            changedFields?.push({ key, savedValue: savedVal, currentValue: currentVal });
          }
        });
        // Check non-numeric fields: itemName, uom
        ['itemName', 'uom']?.forEach(key => {
          const savedVal = savedMat?.[key];
          const currentVal = current?.[key];
          if (savedVal !== undefined && currentVal !== undefined && savedVal !== currentVal) {
            changedFields?.push({ key, savedValue: savedVal, currentValue: currentVal });
          }
        });
        if (changedFields?.length > 0) {
          changes?.push({ id: current?.id, name: savedMat?.itemName || current?.itemName, type: 'material', changedFields, currentMasterData: current });
        }
      });

      // Check Licenses
      (estimate?.selectedLicenses || [])?.forEach((savedLic) => {
        const lookupId = savedLic?.masterId || savedLic?.id;
        let current = currentLicenses?.find(l => l?.id === lookupId);
        if (!current) {
          const fallbackName = savedLic?.softwareName;
          if (fallbackName) current = currentLicenses?.find(l => l?.softwareName?.toLowerCase() === fallbackName?.toLowerCase());
        }
        if (!current) return;
        const changedFields = [];
        const savedCost = savedLic?.monthlyCost ?? savedLic?.cost;
        const currentCost = current?.cost;
        if (savedCost !== undefined && currentCost !== undefined && Math.abs(Number(savedCost) - Number(currentCost)) > 0.0001) {
          changedFields?.push({ key: 'cost', savedValue: savedCost, currentValue: currentCost });
        }
        // Check non-numeric fields: softwareName, licenseType
        ['softwareName', 'licenseType']?.forEach(key => {
          const savedVal = savedLic?.[key];
          const currentVal = current?.[key];
          if (savedVal !== undefined && currentVal !== undefined && savedVal !== currentVal) {
            changedFields?.push({ key, savedValue: savedVal, currentValue: currentVal });
          }
        });
        if (changedFields?.length > 0) {
          changes?.push({ id: current?.id, name: savedLic?.softwareName || current?.softwareName, type: 'license', changedFields, currentMasterData: current });
        }
      });

      // Check Outsource — use masterId (the DB record id) not the generated entry id
      (estimate?.selectedOutsource || [])?.forEach((savedOut) => {
        const lookupId = savedOut?.masterId || savedOut?.id;
        let current = currentOutsource?.find(o => o?.id === lookupId);
        if (!current) {
          const fallbackName = savedOut?.name;
          if (fallbackName) current = currentOutsource?.find(o => o?.name?.toLowerCase() === fallbackName?.toLowerCase());
        }
        if (!current) return;
        const changedFields = [];
        const fieldsToCheck = ['monthlySalary', 'spfValue', 'spfPercentage', 'healthInsuranceEnabled', 'healthInsuranceValue', 'utilizationDefault'];
        fieldsToCheck?.forEach(key => {
          const savedVal = savedOut?.[key];
          const currentVal = current?.[key];
          if (key === 'healthInsuranceEnabled') {
            if (savedVal !== currentVal) {
              changedFields?.push({ key, savedValue: savedVal, currentValue: currentVal });
            }
          } else if (savedVal !== undefined && currentVal !== undefined && Math.abs(Number(savedVal) - Number(currentVal)) > 0.0001) {
            changedFields?.push({ key, savedValue: savedVal, currentValue: currentVal });
          }
        });
        // Check non-numeric fields: role, specialization, nationality
        ['role', 'specialization', 'nationality']?.forEach(key => {
          const savedVal = savedOut?.[key];
          const currentVal = current?.[key];
          if (savedVal !== undefined && currentVal !== undefined && savedVal !== currentVal) {
            changedFields?.push({ key, savedValue: savedVal, currentValue: currentVal });
          }
        });
        if (changedFields?.length > 0) {
          changes?.push({ id: current?.id, name: savedOut?.name || current?.name, type: 'outsource', changedFields, currentMasterData: current });
        }
      });

      if (changes?.length > 0) {
        setSyncChanges(changes);
        setShowSyncBanner(true);
      } else {
        setSyncChanges([]);
        setShowSyncBanner(false);
      }
    } catch (err) {
      console.warn('[MasterDataSync] Detection error:', err?.message);
    }
  };

  const applyChangesToResources = (changesToApply) => {
    // changeMap keyed by master data id (DB UUID from current?.id)
    const changeMap = new Map(changesToApply?.map(c => [c?.id, c]));
    // Secondary name-based map for pre-masterId estimates
    const changeByName = new Map(changesToApply?.map(c => [c?.name?.toLowerCase(), c]));

    const resolveChange = (entry, nameKey) => {
      const lookupId = entry?.masterId || entry?.id;
      return changeMap?.get(lookupId) || changeByName?.get((entry?.[nameKey] || '')?.toLowerCase());
    };

    setSelectedEngineers(prev => prev?.map(eng => {
      const change = resolveChange(eng, 'roleLabel') || resolveChange(eng, 'name');
      if (!change) return eng;
      const updated = { ...eng };
      change?.changedFields?.forEach(f => { updated[f?.key] = f?.currentValue; });
      // Recalculate derived salary fields if monthlySalary changed
      if (change?.changedFields?.some(f => f?.key === 'monthlySalary')) {
        const monthly = updated?.monthlySalary || 0;
        updated.dailySalary = monthly > 0 ? parseFloat((monthly / 30)?.toFixed(6)) : updated?.dailySalary;
        updated.hourlySalary = updated?.dailySalary > 0 ? parseFloat((updated?.dailySalary / 9)?.toFixed(6)) : updated?.hourlySalary;
        updated.costPerHour = updated?.hourlySalary;
      }
      return updated;
    }));

    setSelectedMaterials(prev => prev?.map(mat => {
      const change = resolveChange(mat, 'itemName');
      if (!change) return mat;
      const updated = { ...mat };
      change?.changedFields?.forEach(f => { updated[f?.key] = f?.currentValue; });
      return updated;
    }));

    setSelectedLicenses(prev => prev?.map(lic => {
      const change = resolveChange(lic, 'softwareName');
      if (!change) return lic;
      const updated = { ...lic };
      change?.changedFields?.forEach(f => {
        if (f?.key === 'cost') {
          updated.monthlyCost = f?.currentValue;
          updated.cost = f?.currentValue;
        } else {
          updated[f?.key] = f?.currentValue;
        }
      });
      return updated;
    }));

    setSelectedOutsource(prev => prev?.map(out => {
      const change = resolveChange(out, 'name');
      if (!change) return out;
      const updated = { ...out };
      change?.changedFields?.forEach(f => { updated[f?.key] = f?.currentValue; });
      if (change?.changedFields?.some(f => f?.key === 'monthlySalary')) {
        const monthly = updated?.monthlySalary || 0;
        updated.dailySalary = monthly > 0 ? parseFloat((monthly / 30)?.toFixed(6)) : updated?.dailySalary;
        updated.hourlySalary = updated?.dailySalary > 0 ? parseFloat((updated?.dailySalary / 9)?.toFixed(6)) : updated?.hourlySalary;
        updated.costPerHour = updated?.hourlySalary;
      }
      return updated;
    }));

    setSyncChanges([]);
    setShowSyncBanner(false);
    setShowSyncDialog(false);
    setSyncBannerMinimized(false);
  };

  const handleSyncApplyAll = () => {
    applyChangesToResources(syncChanges);
  };

  const handleSyncApplySelected = (selectedChanges) => {
    applyChangesToResources(selectedChanges);
  };

  const handleSyncKeepOriginal = () => {
    setShowSyncDialog(false);
    setShowSyncBanner(false);
    setSyncChanges([]);
    setSyncBannerMinimized(false);
  };
  // ─────────────────────────────────────────────────────────────────────────────────

  const handleAnalyzeWithAI = async () => {
    if (!showResults) {
      alert('Please calculate the estimate first before analyzing.');
      return;
    }

    setIsAnalyzing(true);
    setShowAIRecommendations(true);
    setAiAnalysis(null);

    try {
      const estimateData = {
        deliveryMode,
        selectedEngineers,
        selectedLicenses,
        selectedMaterials,
        config,
        calculations
      };

      const result = await analyzeEstimate(estimateData);
      setAiAnalysis(result?.data);
    } catch (error) {
      alert(error?.message || 'Failed to analyze estimate. Please try again.');
      setShowAIRecommendations(false);
    } finally {
      setIsAnalyzing(false);
    }
  };

  const handleSaveEstimateClick = () => {
    setValidationError('');
    setShowSaveEstimateModal(true);
  };

  const handleCloseWonDone = (result) => {
    setShowCloseWonModal(false);
    if (result?.success) {
      // Update loadedEstimate deal_status so Won button hides
      setLoadedEstimate(prev => prev ? { ...prev, deal_status: 'Close Won' } : prev);
      setExistingTicket(result?.ticket ? { id: result?.ticket?.id, ticketNumber: result?.ticket?.ticketNumber, status: result?.ticket?.status } : null);
      alert(`Deal marked as Close Won! Incentive Ticket ${result?.ticket?.ticketNumber} created.`);
    }
  };

  return (
    <MainLayout>
      <div className="w-full max-w-7xl mx-auto space-y-6 animate-fade-in overflow-x-hidden">
        {/* Master Data Sync Banner */}
        {showSyncBanner && syncChanges?.length > 0 && !syncBannerMinimized && (
          <div className="flex items-center gap-3 px-4 py-3 bg-warning/10 border border-warning/40 rounded-xl shadow-sm animate-fade-in">
            <div className="p-1.5 rounded-lg bg-warning/20 flex-shrink-0">
              <Icon name="RefreshCw" size={16} color="var(--color-warning)" />
            </div>
            <div className="flex-1 min-w-0">
              <span className="text-sm font-semibold text-warning">
                {syncChanges?.length} resource{syncChanges?.length !== 1 ? 's have' : ' has'} updated Master Data values.
              </span>
              <span className="text-sm text-muted-foreground ml-1">Review and apply?</span>
            </div>
            <button
              onClick={() => setShowSyncDialog(true)}
              className="text-xs font-semibold text-warning underline underline-offset-2 hover:opacity-80 transition-opacity flex-shrink-0"
            >
              Review Changes
            </button>
            <button
              onClick={() => setSyncBannerMinimized(true)}
              className="ml-1 p-1 rounded hover:bg-warning/20 transition-colors flex-shrink-0"
              title="Minimize"
            >
              <Icon name="X" size={14} color="var(--color-warning)" />
            </button>
          </div>
        )}

        {/* Minimized Sync Pill */}
        {showSyncBanner && syncChanges?.length > 0 && syncBannerMinimized && (
          <button
            onClick={() => setSyncBannerMinimized(false)}
            className="fixed bottom-6 right-6 z-50 flex items-center gap-2 px-4 py-2 bg-amber-500 hover:bg-amber-600 text-white text-xs font-semibold rounded-full shadow-lg transition-colors animate-fade-in"
            title="Click to review Master Data updates"
          >
            <span>⚠</span>
            <span>{syncChanges?.length} Master Data update{syncChanges?.length !== 1 ? 's' : ''} pending — Click to Review</span>
          </button>
        )}
        <div className="flex flex-col gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">Service Calculator</h1>
            <p className="text-sm md:text-base text-muted-foreground mt-2">
              Quick project cost estimation with real-time calculations
            </p>
            {isViewer && (
              <div className="mt-2 inline-flex items-center gap-1.5 px-3 py-1 bg-warning/10 border border-warning/30 rounded-full">
                <Icon name="Eye" size={12} color="var(--color-warning)" />
                <span className="text-xs text-warning font-medium">View Only — You do not have edit permissions</span>
              </div>
            )}
            {loadedEstimateId && !isViewer && (
              <div className="mt-2 inline-flex items-center gap-2 px-3 py-1.5 bg-accent/10 border border-accent/30 rounded-full">
                <Icon name="Pencil" size={12} color="var(--color-accent)" />
                <span className="text-xs text-accent font-medium">
                  Editing: <span className="font-bold">{loadedEstimate?.name || 'Saved Estimate'}</span>
                </span>
                <button
                  onClick={() => { setLoadedEstimateId(null); setLoadedEstimate(null); }}
                  className="ml-1 hover:opacity-70 transition-opacity"
                  title="Discard edit mode (keep changes, save as new)"
                >
                  <Icon name="X" size={10} color="var(--color-accent)" />
                </button>
              </div>
            )}
          </div>
          <div className="flex flex-wrap gap-2 items-center">
            {/* Currency Selector */}
            <div className="flex items-center gap-2 bg-card border border-border rounded-lg px-3 py-2 shadow-sm">
              <Icon name="DollarSign" size={16} color="var(--color-primary)" />
              <span className="text-xs font-medium text-muted-foreground hidden sm:inline">Currency:</span>
              <select
                value={currency}
                onChange={(e) => setCurrency(e?.target?.value)}
                disabled={isViewer}
                className="text-sm font-semibold text-foreground bg-transparent border-none outline-none cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed max-w-[140px]"
              >
                {CURRENCIES?.map(c => (
                  <option key={c?.code} value={c?.code}>{c?.code} — {c?.name}</option>
                ))}
              </select>
            </div>
            <Button
              variant="outline"
              iconName="GitBranch"
              iconPosition="left"
              onClick={() => setShowWhatIfScenarios(true)}
              disabled={!showResults}
            >
              <span className="hidden sm:inline">What-If</span>
              <span className="sm:hidden">What-If</span>
            </Button>
            <Button
              variant="outline"
              iconName="GitCompare"
              iconPosition="left"
              onClick={() => setShowComparisonView(true)}
            >
              <span className="hidden sm:inline">Compare</span>
              <span className="sm:hidden">Compare</span>
            </Button>
            <Button
              variant="outline"
              iconName="History"
              iconPosition="left"
              onClick={() => setShowEstimateHistory(true)}
            >
              <span className="hidden sm:inline">History</span>
              <span className="sm:hidden">History</span>
            </Button>
            <Button
              variant="outline"
              iconName="FolderOpen"
              iconPosition="left"
              onClick={() => setShowTemplateLibrary(true)}
            >
              <span className="hidden sm:inline">Templates</span>
              <span className="sm:hidden">Templates</span>
            </Button>
            {!isViewer && (
              <Button
                variant="outline"
                iconName="Save"
                iconPosition="left"
                onClick={() => setShowSaveTemplateModal(true)}
                className={!permissions?.canManageCostEstimation ? 'hidden' : ''}
              >
                <span className="hidden sm:inline">Save Template</span>
                <span className="sm:hidden">Save Tmpl</span>
              </Button>
            )}
            {!isViewer && (
              <Button
                variant="outline"
                iconName="RotateCcw"
                iconPosition="left"
                onClick={handleReset}
              >
                Reset
              </Button>
            )}
            {showResults && (
              <>
                {permissions?.canManageCostEstimation && !isViewer && (
                  <Button
                    variant="default"
                    iconName="Save"
                    iconPosition="left"
                    onClick={handleSaveEstimateClick}
                  >
                    <span className="hidden sm:inline">Save Estimate</span>
                    <span className="sm:hidden">Save</span>
                  </Button>
                )}
                {/* Won button — shown when a saved estimate is loaded and no ticket exists yet.
                    Relies on existingTicket (live DB check) as source of truth, not stale deal_status. */}
                {loadedEstimateId && !existingTicket && !isViewer && (
                  <Button
                    variant="default"
                    iconName="Trophy"
                    iconPosition="left"
                    onClick={() => setShowCloseWonModal(true)}
                    className="bg-success hover:bg-success/90 text-white border-success"
                  >
                    <span className="hidden sm:inline">Won</span>
                    <span className="sm:hidden">Won</span>
                  </Button>
                )}
                {/* View Ticket button — shown when ticket already exists */}
                {loadedEstimateId && existingTicket && (
                  <Button
                    variant="outline"
                    iconName="Ticket"
                    iconPosition="left"
                    onClick={() => navigate(`/incentive-tickets/${existingTicket?.id}`)}
                    className="border-success/50 text-success hover:bg-success/10"
                  >
                    <span className="hidden sm:inline">View Ticket</span>
                    <span className="sm:hidden">Ticket</span>
                  </Button>
                )}
                <Button
                  variant="success"
                  iconName="Download"
                  iconPosition="left"
                  onClick={() => setShowExportModal(true)}
                >
                  Export
                </Button>
              </>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
          <div className="space-y-6 min-w-0">
            <div className={`bg-gradient-to-br from-card via-card to-muted/10 border border-border rounded-2xl p-4 md:p-6 shadow-xl hover:shadow-2xl transition-all duration-smooth ${isViewer ? 'pointer-events-none opacity-80' : ''}`}>
              <div className="flex items-center gap-3 mb-6 pb-4 border-b border-border/50">
                <div className="p-2 rounded-xl bg-gradient-to-br from-primary to-accent shadow-lg flex-shrink-0">
                  <Icon name="Settings" size={24} color="white" />
                </div>
                <h2 className="text-lg font-semibold text-foreground">Input Configuration</h2>
                {isViewer && (
                  <span className="ml-auto text-xs text-muted-foreground flex items-center gap-1">
                    <Icon name="Lock" size={12} color="var(--color-muted-foreground)" />
                    Read-only
                  </span>
                )}
              </div>

              <div className="space-y-6">
                <DeliveryModeSelector
                  selectedMode={deliveryMode}
                  onModeChange={setDeliveryMode}
                />

                <div className="border-t border-border pt-6">
                  <EngineerSelector
                    selectedEngineers={selectedEngineers}
                    onEngineersChange={setSelectedEngineers}
                    currency={selectedCurrency}
                  />
                </div>

                <div className="border-t border-border pt-6">
                  <LicenseSelector
                    selectedLicenses={selectedLicenses}
                    onLicensesChange={setSelectedLicenses}
                    currency={selectedCurrency}
                  />
                </div>

                <div className="border-t border-border pt-6">
                  <MaterialSelector
                    selectedMaterials={selectedMaterials}
                    onMaterialsChange={setSelectedMaterials}
                    currency={selectedCurrency}
                  />
                </div>

                <div className="border-t border-border pt-6">
                  <OutsourceSelector
                    selectedOutsource={selectedOutsource}
                    onOutsourceChange={setSelectedOutsource}
                    currency={selectedCurrency}
                    syncChanges={syncChanges}
                  />
                </div>

                <div className="border-t border-border pt-6">
                  <ConfigurationPanel
                    config={config}
                    onConfigChange={setConfig}
                  />
                </div>

                <Button
                  variant="default"
                  fullWidth
                  iconName="Calculator"
                  iconPosition="left"
                  onClick={handleCalculate}
                  disabled={isViewer}
                >
                  {isViewer ? 'View Only Mode' : 'Calculate Estimate'}
                </Button>
                {validationError && (
                  <div className="flex items-center gap-2 px-3 py-2 bg-error/10 border border-error/30 rounded-lg">
                    <Icon name="AlertCircle" size={14} color="var(--color-error)" />
                    <p className="text-xs text-error">{validationError}</p>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="space-y-6 min-w-0">
            <div className="bg-gradient-to-br from-card via-card to-primary/5 border border-border rounded-2xl p-4 md:p-6 shadow-xl hover:shadow-2xl transition-all duration-smooth">
              <div className="flex items-center gap-3 mb-6 pb-4 border-b border-border/50">
                <div className="p-2 rounded-xl bg-gradient-to-br from-accent to-primary shadow-lg flex-shrink-0">
                  <Icon name="BarChart3" size={24} color="white" />
                </div>
                <h2 className="text-lg font-semibold text-foreground">Calculation Output</h2>
              </div>

              {showResults ? (
                <CalculationResults calculations={calculations} currency={selectedCurrency} pricingBasis={calculations?.pricingBasis} />
              ) : (
                <div className="flex flex-col items-center justify-center min-h-[300px] md:min-h-[500px] text-center py-12">
                  <div className="p-6 rounded-2xl bg-gradient-to-br from-muted to-muted/50 shadow-inner-lg mb-6">
                    <Icon name="Calculator" size={64} color="var(--color-muted-foreground)" />
                  </div>
                  <h3 className="text-lg font-semibold text-foreground mt-6">No Calculations Yet</h3>
                  <p className="text-sm text-muted-foreground mt-2 max-w-md px-4">
                    Configure your project requirements in the input panel and click "Calculate Estimate" to see real-time cost calculations with Excel-matching logic.
                  </p>
                </div>
              )}
            </div>

            {showResults && (
              <div className="bg-gradient-to-r from-primary/10 via-accent/10 to-primary/10 border border-primary/30 rounded-2xl p-4 shadow-lg animate-fade-in">
                <div className="flex items-start gap-3">
                  <div className="p-2 rounded-lg bg-primary/20 flex-shrink-0">
                    <Icon name="Info" size={20} color="var(--color-primary)" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold text-foreground mb-1">Calculation Notes</div>
                    <ul className="text-xs text-muted-foreground space-y-1 list-disc list-inside">
                      <li>Delivery mode factor: {deliveryModeMultipliers?.[deliveryMode]}x applied to labor costs</li>
                      <li>Material waste percentages included in total costs</li>
                      <li>License costs calculated based on subscription or perpetual type</li>
                      <li>Overhead, contingency, and margin applied per configuration</li>
                    </ul>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      <SaveTemplateModal
        isOpen={showSaveTemplateModal}
        onClose={() => setShowSaveTemplateModal(false)}
        onSave={handleSaveTemplate}
        currentConfig={{ deliveryMode, selectedEngineers, selectedLicenses, selectedMaterials, config }}
      />
      <TemplateLibrary
        isOpen={showTemplateLibrary}
        onClose={() => setShowTemplateLibrary(false)}
        onLoadTemplate={handleLoadTemplate}
      />
      <EstimateHistory
        isOpen={showEstimateHistory}
        onClose={() => setShowEstimateHistory(false)}
        onLoadEstimate={handleLoadEstimate}
        onEditEstimate={handleEditEstimate}
      />
      <SaveEstimateModal
        isOpen={showSaveEstimateModal}
        onClose={() => setShowSaveEstimateModal(false)}
        onSave={handleSaveEstimate}
        estimateData={{ deliveryMode, selectedEngineers, selectedLicenses, selectedMaterials, selectedOutsource, config, calculations, currency }}
        currency={selectedCurrency}
        editingEstimate={loadedEstimate}
      />
      {showAIRecommendations && (
        <AIRecommendationsModal
          isOpen={showAIRecommendations}
          onClose={() => setShowAIRecommendations(false)}
          analysis={aiAnalysis}
          isLoading={isAnalyzing}
        />
      )}
      {showExportModal && (
        <ExportModal
          isOpen={showExportModal}
          onClose={() => setShowExportModal(false)}
          calculations={calculations}
          deliveryMode={deliveryMode}
          selectedEngineers={selectedEngineers}
          selectedLicenses={selectedLicenses}
          selectedMaterials={selectedMaterials}
          selectedOutsource={selectedOutsource}
          config={config}
          currency={selectedCurrency}
          pricingBasis={calculations?.pricingBasis}
          savedPtcName={lastSavedPtcName}
          savedPtcNumber={lastSavedPtcNumber}
        />
      )}
      {showComparisonView && (
        <ComparisonView
          isOpen={showComparisonView}
          onClose={() => setShowComparisonView(false)}
        />
      )}
      {showWhatIfScenarios && (
        <WhatIfScenarioManager
          isOpen={showWhatIfScenarios}
          onClose={() => setShowWhatIfScenarios(false)}
          baseConfig={{
            deliveryMode,
            selectedEngineers,
            selectedLicenses,
            selectedMaterials,
            config
          }}
          baseCalculations={calculations}
        />
      )}
      {showCloseWonModal && loadedEstimate && (
        <CloseWonModal
          isOpen={showCloseWonModal}
          onClose={handleCloseWonDone}
          estimate={loadedEstimate}
          calculations={calculations}
        />
      )}
      <MasterDataSyncDialog
        isOpen={showSyncDialog}
        changes={syncChanges}
        onApplyAll={handleSyncApplyAll}
        onApplySelected={handleSyncApplySelected}
        onKeepOriginal={handleSyncKeepOriginal}
        onMinimize={() => { setShowSyncDialog(false); setSyncBannerMinimized(true); }}
      />
    </MainLayout>
  );
};

export default ServiceCalculator;